import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { WardrobeItem, SubscriptionTier } from '../types/synclothe';
import { useToasts } from './TactileToasts';
import MultiProviderLogin from './MultiProviderLogin';

interface WardrobeManagerProps {
  userId?: string;
  subscriptionTier: SubscriptionTier;
}

const CATEGORIES = ['All', 'Tops', 'Bottoms', 'Full-Body', 'Outerwear', 'Other'];
const EDIT_CATEGORIES = ['Tops', 'Bottoms', 'Full-Body', 'Outerwear', 'Other'];

const WardrobeManager: React.FC<WardrobeManagerProps> = ({ userId, subscriptionTier }) => {
  const { showToast } = useToasts();
  const isPremium = subscriptionTier === 'Premium';

  const [wardrobe, setWardrobe] = useState<WardrobeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [isCropped, setIsCropped] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  // Crop box in percentage (0-100) relative to displayed image
  const [cropRect, setCropRect] = useState<{ x: number; y: number; width: number; height: number }>({
    x: 8,
    y: 8,
    width: 84,
    height: 84
  });
  const cropImgRef = useRef<HTMLImageElement>(null);
  const cropDragRef = useRef<{
    mode: 'move' | 'nw' | 'ne' | 'sw' | 'se' | null;
    startX: number;
    startY: number;
    startRect: { x: number; y: number; width: number; height: number };
  }>({ mode: null, startX: 0, startY: 0, startRect: { x: 8, y: 8, width: 84, height: 84 } });

  const [uploading, setUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  
  const [isHoveringStack, setIsHoveringStack] = useState(false);
  const [filterCategory, setFilterCategory] = useState('All');
  const [currentShelf, setCurrentShelf] = useState(0);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<WardrobeItem | null>(null);
  const [culturalTagsInput, setCulturalTagsInput] = useState('');

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  interface PendingDeletion {
    item: WardrobeItem;
    timeoutId: NodeJS.Timeout;
    secondsRemaining: number;
  }
  const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(null);
  const pendingDeletionRef = useRef<PendingDeletion | null>(null);
  pendingDeletionRef.current = pendingDeletion;

  // Cleanup and finalize deletion on unmount if pending
  useEffect(() => {
    return () => {
      if (pendingDeletionRef.current) {
        clearTimeout(pendingDeletionRef.current.timeoutId);
        const item = pendingDeletionRef.current.item;
        fetch(`/api/deleteWardrobeItem/${item.id}?userId=${userId}&tier=${subscriptionTier}`, { 
          method: 'DELETE', 
          headers: { 'x-user-id': userId || '', 'x-selected-tier': subscriptionTier as string } 
        }).catch(() => {});
      }
    };
  }, [userId, subscriptionTier]);

  // 1-second countdown ticker for deletion HUD
  useEffect(() => {
    if (!pendingDeletion) return;
    const interval = setInterval(() => {
      setPendingDeletion(prev => {
        if (!prev) return null;
        if (prev.secondsRemaining <= 1) return prev;
        return { ...prev, secondsRemaining: prev.secondsRemaining - 1 };
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDeletion?.item?.id]);

  const fetchWardrobe = useCallback(async () => {
    if (!userId || !isPremium) {
        setLoading(false);
        return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/getWardrobe?userId=${userId}&tier=${subscriptionTier}`, {
        headers: { 'x-user-id': userId || '' }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Fetch failed");
      setWardrobe(data);
    } catch (err: any) {
      setError(err.message);
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [userId, isPremium, subscriptionTier, showToast]);

  const lastWardrobeFetchTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    fetchWardrobe();
    lastWardrobeFetchTimeRef.current = Date.now();
  }, [fetchWardrobe]);

  // Idle Return & Hardware Safety: Stop camera on background tab, refresh SAS tokens if elapsed > 45 mins
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stopCamera();
      } else if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastWardrobeFetchTimeRef.current;
        if (elapsed > 45 * 60 * 1000) {
          fetchWardrobe();
          lastWardrobeFetchTimeRef.current = Date.now();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchWardrobe]);

  // --- LOGICAL SHELVING SYSTEM ---
  const isMobile = window.innerWidth < 768;
  const itemsPerShelf = isMobile ? 5 : 12;

  const filteredWardrobe = filterCategory === 'All' 
    ? wardrobe 
    : filterCategory.toLowerCase() === 'other'
      ? wardrobe.filter(item => {
          const cat = (item.category || '').toLowerCase();
          return cat === 'other' || cat === 'accessory' || cat === 'footwear';
        })
      : wardrobe.filter(item => item.category && item.category.toLowerCase() === filterCategory.toLowerCase());

  // Split filtered wardrobe into logical shelves based on screen capacity
  const shelves: WardrobeItem[][] = [];
  for (let i = 0; i < filteredWardrobe.length; i += itemsPerShelf) {
    shelves.push(filteredWardrobe.slice(i, i + itemsPerShelf));
  }

  // Effect: Reset shelf index if the category filter changes
  useEffect(() => {
    setCurrentShelf(0);
  }, [filterCategory]);

  const activeShelfItems = shelves[currentShelf] || [];
  const selectedItem = wardrobe.find(i => i.id === selectedItemId);

  // Camera logic
  const streamRef = useRef<MediaStream | null>(null);

  const startCamera = async () => {
    if (!isPremium) return;
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      setShowCamera(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setError("Camera access denied or unavailable.");
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setShowCamera(false);
  };

  useEffect(() => {
    if (showCamera && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [showCamera]);

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], "captured-item.jpg", { type: "image/jpeg" });
          const previewUrl = URL.createObjectURL(file);
          setSelectedFile(file);
          setFilePreview(previewUrl);
          setOriginalFile(file);
          setOriginalPreview(previewUrl);
          setIsCropped(false);
          setCropRect({ x: 8, y: 8, width: 84, height: 84 });
          stopCamera();
        }
      }, 'image/jpeg', 0.9);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const previewUrl = URL.createObjectURL(file);
      setSelectedFile(file);
      setFilePreview(previewUrl);
      setOriginalFile(file);
      setOriginalPreview(previewUrl);
      setIsCropped(false);
      setCropRect({ x: 8, y: 8, width: 84, height: 84 });
    }
  };

  const openCropModal = () => {
    if (!originalPreview && !filePreview) return;
    setShowCropModal(true);
  };

  const applyCropPreset = (preset: 'free' | '3:4' | '1:1' | 'full') => {
    if (preset === 'full') {
      setCropRect({ x: 0, y: 0, width: 100, height: 100 });
      return;
    }
    if (preset === 'free') {
      setCropRect({ x: 8, y: 8, width: 84, height: 84 });
      return;
    }
    const imgEl = cropImgRef.current;
    if (!imgEl || !imgEl.clientWidth || !imgEl.clientHeight) {
      setCropRect({ x: 10, y: 10, width: 80, height: 80 });
      return;
    }
    const imgAspect = imgEl.clientWidth / imgEl.clientHeight;
    const targetAspect = preset === '3:4' ? 3 / 4 : 1;
    let wPct = 84;
    let hPct = 84;
    if (imgAspect > targetAspect) {
      // Image is wider than target aspect
      hPct = 86;
      wPct = Math.min(96, (hPct * targetAspect) / imgAspect);
    } else {
      // Image is taller than target aspect
      wPct = 86;
      hPct = Math.min(96, (wPct * imgAspect) / targetAspect);
    }
    setCropRect({
      x: Math.max(0, (100 - wPct) / 2),
      y: Math.max(0, (100 - hPct) / 2),
      width: wPct,
      height: hPct
    });
  };

  const handleCropPointerDown = (
    mode: 'move' | 'nw' | 'ne' | 'sw' | 'se',
    clientX: number,
    clientY: number,
    e: React.MouseEvent | React.TouchEvent
  ) => {
    e.stopPropagation();
    cropDragRef.current = {
      mode,
      startX: clientX,
      startY: clientY,
      startRect: { ...cropRect }
    };
  };

  const handleCropPointerMove = (clientX: number, clientY: number) => {
    const drag = cropDragRef.current;
    if (!drag.mode || !cropImgRef.current) return;

    const bounds = cropImgRef.current.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;

    const dxPct = ((clientX - drag.startX) / bounds.width) * 100;
    const dyPct = ((clientY - drag.startY) / bounds.height) * 100;
    const minSize = 15;
    const r = { ...drag.startRect };

    if (drag.mode === 'move') {
      const nextX = Math.max(0, Math.min(100 - r.width, r.x + dxPct));
      const nextY = Math.max(0, Math.min(100 - r.height, r.y + dyPct));
      setCropRect({ ...r, x: nextX, y: nextY });
    } else if (drag.mode === 'se') {
      const nextW = Math.max(minSize, Math.min(100 - r.x, r.width + dxPct));
      const nextH = Math.max(minSize, Math.min(100 - r.y, r.height + dyPct));
      setCropRect({ ...r, width: nextW, height: nextH });
    } else if (drag.mode === 'nw') {
      const maxDx = r.width - minSize;
      const clampedDx = Math.max(-r.x, Math.min(maxDx, dxPct));
      const maxDy = r.height - minSize;
      const clampedDy = Math.max(-r.y, Math.min(maxDy, dyPct));
      setCropRect({
        x: r.x + clampedDx,
        y: r.y + clampedDy,
        width: r.width - clampedDx,
        height: r.height - clampedDy
      });
    } else if (drag.mode === 'ne') {
      const nextW = Math.max(minSize, Math.min(100 - r.x, r.width + dxPct));
      const maxDy = r.height - minSize;
      const clampedDy = Math.max(-r.y, Math.min(maxDy, dyPct));
      setCropRect({
        x: r.x,
        y: r.y + clampedDy,
        width: nextW,
        height: r.height - clampedDy
      });
    } else if (drag.mode === 'sw') {
      const maxDx = r.width - minSize;
      const clampedDx = Math.max(-r.x, Math.min(maxDx, dxPct));
      const nextH = Math.max(minSize, Math.min(100 - r.y, r.height + dyPct));
      setCropRect({
        x: r.x + clampedDx,
        y: r.y,
        width: r.width - clampedDx,
        height: nextH
      });
    }
  };

  const handleCropPointerUp = () => {
    cropDragRef.current.mode = null;
  };

  const applyCrop = () => {
    const imgEl = cropImgRef.current;
    if (!imgEl || !imgEl.naturalWidth || !imgEl.naturalHeight) return;

    const sx = Math.round((cropRect.x / 100) * imgEl.naturalWidth);
    const sy = Math.round((cropRect.y / 100) * imgEl.naturalHeight);
    const sw = Math.max(1, Math.round((cropRect.width / 100) * imgEl.naturalWidth));
    const sh = Math.max(1, Math.round((cropRect.height / 100) * imgEl.naturalHeight));

    // Cap output dimensions at 1600px max to respect iOS Safari canvas memory limits on 24MP/48MP camera photos
    const maxCanvasDim = 1600;
    const scale = Math.min(1, maxCanvasDim / Math.max(sw, sh));
    const targetW = Math.max(1, Math.round(sw * scale));
    const targetH = Math.max(1, Math.round(sh * scale));

    const offscreen = document.createElement('canvas');
    offscreen.width = targetW;
    offscreen.height = targetH;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(imgEl, sx, sy, sw, sh, 0, 0, targetW, targetH);
    offscreen.toBlob((blob) => {
      if (blob) {
        const croppedFile = new File([blob], originalFile?.name || 'cropped-item.jpg', { type: 'image/jpeg' });
        const croppedUrl = URL.createObjectURL(croppedFile);
        setSelectedFile(croppedFile);
        setFilePreview(croppedUrl);
        setIsCropped(true);
        setShowCropModal(false);
        showToast("Garment frame cropped. Click Confirm to digitize.", "logic");
      }
    }, 'image/jpeg', 0.92);
  };

  const resetToOriginalImage = () => {
    if (originalFile && originalPreview) {
      setSelectedFile(originalFile);
      setFilePreview(originalPreview);
      setIsCropped(false);
      setCropRect({ x: 8, y: 8, width: 84, height: 84 });
      showToast("Restored original uncropped image.", "logic");
    }
  };

  const clearPendingSelection = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setOriginalFile(null);
    setOriginalPreview(null);
    setIsCropped(false);
    setShowCropModal(false);
  };

  const handleUpload = async () => {
    if (!selectedFile || !isPremium) return;
    setUploading(true);
    setError(null);
    try {
      const resizedBase64 = await resizeImage(selectedFile, 1200);
      const analyzeResponse = await fetch('/api/analyzeWardrobe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': userId || '' },
        body: JSON.stringify({ imageData: resizedBase64, contentType: "image/jpeg" })
      });
      const metadata = await analyzeResponse.json();
      if (!analyzeResponse.ok) {
        throw new Error(metadata.error || "Wardrobe analysis failed");
      }

      if (metadata.isApparel === false) {
        throw new Error(`Rejection: ${metadata.rejectionReason || "AI determined this is not a clothing item."}`);
      }

      const uploadResponse = await fetch('/api/wardrobe/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': userId || '', 'x-selected-tier': subscriptionTier as string },
        body: JSON.stringify({ imageData: resizedBase64, contentType: "image/jpeg", metadata })
      });
      const savedItem = await uploadResponse.json();
      if (!uploadResponse.ok) throw new Error(savedItem.error || "Upload failed");

      setWardrobe(prev => [...prev, savedItem]);
      clearPendingSelection();
      showToast("Object successfully digitized.", "success");
    } catch (err: any) {
      showToast(`Digitization Error: ${err.message}`, "error");
    } finally {
      setUploading(false);
    }
  };

  const resizeImage = (file: File, maxWidth: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) { height = (maxWidth / width) * height; width = maxWidth; }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.85).split(',')[1]);
        };
        img.onerror = () => reject(new Error("Unable to decode mobile image file"));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error("Unable to read mobile file"));
      reader.readAsDataURL(file);
    });
  };

  const handleDelete = async (itemId: string) => {
    if (!isPremium) return;
    const itemToDelete = wardrobe.find(i => i.id === itemId);
    if (!itemToDelete) return;

    // If an earlier item is still pending deletion, commit it immediately
    if (pendingDeletionRef.current) {
      clearTimeout(pendingDeletionRef.current.timeoutId);
      const prevItem = pendingDeletionRef.current.item;
      fetch(`/api/deleteWardrobeItem/${prevItem.id}?userId=${userId}&tier=${subscriptionTier}`, { 
        method: 'DELETE', 
        headers: { 'x-user-id': userId || '', 'x-selected-tier': subscriptionTier as string } 
      }).catch(() => {});
    }

    // Optimistically hide from current wardrobe view
    setWardrobe(prev => prev.filter(i => i.id !== itemId));
    if (selectedItemId === itemId) setSelectedItemId(null);

    // 5-second countdown timer before permanent backend eradication
    const timeoutId = setTimeout(async () => {
      try {
        await fetch(`/api/deleteWardrobeItem/${itemId}?userId=${userId}&tier=${subscriptionTier}`, { 
          method: 'DELETE', 
          headers: { 'x-user-id': userId || '', 'x-selected-tier': subscriptionTier as string } 
        });
      } catch (err: any) {
        showToast(`Deletion error: ${err.message}`, 'error');
      } finally {
        setPendingDeletion(null);
      }
    }, 5000);

    setPendingDeletion({
      item: itemToDelete,
      timeoutId,
      secondsRemaining: 5
    });

    showToast(`"${itemToDelete.name}" marked for deletion (Undo available for 5s).`, "sync");
  };

  const handleUndoDelete = () => {
    if (!pendingDeletion) return;
    clearTimeout(pendingDeletion.timeoutId);
    setWardrobe(prev => [pendingDeletion.item, ...prev]);
    setSelectedItemId(pendingDeletion.item.id);
    const restoredName = pendingDeletion.item.name;
    setPendingDeletion(null);
    showToast(`Restored "${restoredName}" to digital closet.`, "success");
  };

  const handleEditInit = () => {
      const item = wardrobe.find(i => i.id === selectedItemId);
      if (item) {
          setEditForm({ ...item });
          setCulturalTagsInput(item.culturalTags?.join(', ') || '');
          setIsEditing(true);
      }
  };

  const handleEditSave = async () => {
    if (!editForm || !isPremium) return;

    // Finalize the tags from the input string just before saving
    const finalTags = culturalTagsInput.split(',').map(t => t.trim()).filter(t => t);
    const payload = { ...editForm, culturalTags: finalTags };
    
    try {
        const response = await fetch(`/api/updateWardrobeItem/${editForm.id}?userId=${userId}&tier=${subscriptionTier}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'x-user-id': userId || '', 'x-selected-tier': subscriptionTier as string },
            body: JSON.stringify(payload)
        });
        const updatedItem = await response.json();
        if (!response.ok) throw new Error(updatedItem.error || "Update failed");

        setWardrobe(prev => prev.map(i => i.id === updatedItem.id ? updatedItem : i));
        setIsEditing(false);
        setEditForm(null);
        showToast("Metadata refinement complete.", "success");
    } catch (err: any) {
        showToast(`Refinement Error: ${err.message}`, "error");
    }
  };

  if (!userId) {
    return (
      <div className="p-4 md:p-8 min-h-screen bg-dk-900 flex flex-col items-center justify-center text-center">
        <div className="max-w-md space-y-6">
          <div className="w-20 h-20 bg-azure/10 rounded-full flex items-center justify-center mx-auto border border-azure/20 animate-pulse">
            <svg className="w-10 h-10 text-azure-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
          </div>
          <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">Authentication Required</h2>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-relaxed">Please sign in to access your Digital Closet.</p>
          
          <MultiProviderLogin className="mt-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 min-h-screen bg-dk-900 text-gray-200 font-inter relative overflow-hidden read-only-ui">
      
      {!isPremium && (
          <div className="absolute inset-0 z-50 bg-dk-900/60 backdrop-blur-md flex items-center justify-center p-4 md:p-8">
              <div className="glass-panel max-w-lg w-full p-8 md:p-12 rounded-[2rem] text-center shadow-[0_0_100px_rgba(251,191,36,0.1)] border-gold/20 animate-fade-in">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6 md:mb-8 border border-gold/30">
                    <svg className="w-8 h-8 md:w-10 md:h-10 text-gold" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"></path></svg>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-black text-white italic uppercase tracking-tighter mb-4">Digital Closet Locked</h2>
                  <p className="text-gray-300 text-[11px] md:text-sm leading-relaxed mb-8">
                      Your high-fidelity Digital Twin closet is only accessible in the **Premium Tier**. 
                      Digitize your entire physical wardrobe and unlock AI-powered "On-Model" visualization.
                  </p>
                  <Link to="/subscription" className="btn-azure bg-gold hover:bg-gold-500 text-dk-900 w-full shadow-gold/20 tracking-[0.2em] mb-4 text-[10px] block text-center">Upgrade to Premium</Link>
              </div>
          </div>
      )}

      <div className={`max-w-4xl mx-auto flex flex-col gap-8 md:gap-12 animate-fade-in ${!isPremium ? 'opacity-20 pointer-events-none grayscale' : ''}`}>
        
        <div className="relative">
          <header className="mb-8 md:mb-12 border-b border-white/5 pb-6 md:pb-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
            <div>
              <span className="text-azure-400 text-[9px] md:text-[10px] font-black uppercase tracking-[0.4em] mb-2 block">Logic Core: Inventory</span>
              <h1 className="text-2xl md:text-4xl font-black text-white uppercase italic tracking-tighter leading-none">Digital <span className="text-azure-500">Closet</span></h1>
            </div>
            <div className="flex gap-3 md:gap-4">
                <button onClick={startCamera} className="hidden md:flex btn-azure text-[9px] md:text-[10px] uppercase tracking-widest shadow-azure/20 items-center gap-2 py-2 px-4 md:py-3 md:px-6">
                   <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"></path></svg>
                   Capture
                </button>
                <label className="flex md:hidden btn-azure text-[9px] uppercase tracking-widest shadow-azure/20 cursor-pointer items-center gap-2 py-2 px-4">
                   <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"></path></svg>
                   Capture
                   <input type="file" accept="image/*" capture="environment" onChange={handleFileChange} className="hidden" />
                </label>
                <label className="btn-azure bg-white/5 border border-white/10 hover:bg-white/10 text-[9px] md:text-[10px] uppercase tracking-widest cursor-pointer shadow-none flex items-center gap-2 py-2 px-4 md:py-3 md:px-6">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"></path></svg>
                  <span>Upload</span>
                  <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                </label>
            </div>
          </header>

          {/* Upload Guidelines */}
          <div className="mb-8 p-5 bg-azure/5 rounded-xl border border-azure/20 text-[10px] text-gray-400 uppercase tracking-widest leading-relaxed max-w-4xl mx-auto flex items-start gap-4 shadow-xl">
            <svg className="w-5 h-5 text-azure-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            <div>
              <p className="font-black text-azure-400 mb-2">Digitization Guidelines:</p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-500 font-bold">
                <li>Upload flat-lay photos of clothes (on a hanger, floor, or table).</li>
                <li>Ensure the photo contains ONLY the clothing item.</li>
                <li><span className="text-azure-400">CRITICAL:</span> Do NOT upload photos containing human faces. Files with faces will be automatically rejected.</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-8 md:mb-12 animate-fade-in">
             {CATEGORIES.map(cat => (
                <button 
                  key={cat} 
                  onClick={() => { setFilterCategory(cat); setSelectedItemId(null); setIsEditing(false); }}
                  className={`px-3 md:px-4 py-1.5 rounded-full text-[8px] md:text-[9px] font-black uppercase tracking-widest transition-all duration-300 border
                    ${filterCategory === cat 
                        ? 'bg-azure text-white border-azure shadow-[0_0_15px_rgba(59,130,246,0.4)]' 
                        : 'bg-white/5 text-gray-500 border-white/5 hover:border-white/20 hover:text-gray-300'}
                  `}
                >
                    {cat}
                </button>
             ))}
          </div>

          {showCamera && (
            <div className="fixed inset-0 z-[100] bg-dk-900/95 backdrop-blur-xl flex items-center justify-center p-8">
                <div className="glass-panel rounded-3xl overflow-hidden w-full max-w-2xl relative shadow-2xl">
                    <video ref={videoRef} autoPlay playsInline className="w-full aspect-[4/3] object-cover bg-black" />
                    <canvas ref={canvasRef} className="hidden" />
                    <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-6">
                        <button onClick={stopCamera} className="bg-white/10 hover:bg-white/20 text-white p-4 rounded-full backdrop-blur-md transition-colors">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                        <button onClick={capturePhoto} className="bg-azure hover:bg-azure-600 text-white p-4 rounded-full shadow-lg shadow-azure/50 transition-all transform hover:scale-110 active:scale-95">
                            <div className="w-6 h-6 border-4 border-white rounded-full"></div>
                        </button>
                    </div>
                </div>
            </div>
          )}

          {error && <div className="mb-8 p-6 glass-panel border-red-500/50 text-red-400 text-[11px] font-bold uppercase tracking-widest leading-relaxed shadow-lg bg-red-500/5 animate-fade-in flex items-start gap-4">
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path></svg>
              <span>{error}</span>
          </div>}

          {uploading && (
            <div className="mb-8 p-6 glass-panel border-azure/30 flex items-center gap-6 animate-pulse bg-azure/5">
              <div className="w-4 h-4 bg-azure rounded-full animate-ping"></div>
              <span className="text-xs font-black uppercase tracking-[0.3em] text-azure-400">AI Digitizing...</span>
            </div>
          )}

          {selectedFile && !uploading && (
            <div className="mb-8 p-6 glass-panel border-gold/30 flex flex-col sm:flex-row items-start sm:items-center gap-6 animate-fade-in bg-gold/5 shadow-xl">
              <div
                onClick={openCropModal}
                title="Click dress image to crop"
                className="relative group cursor-pointer flex-shrink-0 rounded-xl overflow-hidden border border-gold/40 shadow-lg"
              >
                <img src={filePreview!} alt="Pending" className="w-24 h-24 object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                  <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 3v12a2 2 0 002 2h12M3 7h12a2 2 0 012 2v12" />
                  </svg>
                  <span className="text-[8px] font-black uppercase tracking-widest text-gold">Crop</span>
                </div>
                <div className="
                  absolute bottom-1 right-1 bg-black/80 border border-gold/40 rounded px-1.5 py-0.5
                  text-[7px] font-black uppercase tracking-widest text-gold pointer-events-none
                ">
                  {isCropped ? 'Cropped' : 'Tap to Crop'}
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <p className="text-[10px] text-gold uppercase font-black tracking-widest italic">Awaiting Authorization</p>
                  {isCropped && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 text-[8px] font-black uppercase tracking-widest">
                      Cropped Image Active
                    </span>
                  )}
                </div>
                <p className="text-[9px] text-gray-400 uppercase tracking-wider mb-3">
                  Click the garment photo or select <span className="text-gold font-bold">Crop</span> to isolate the dress before confirming.
                </p>
                <div className="flex flex-wrap gap-2.5">
                    <button onClick={handleUpload} className="btn-azure py-2.5 px-6 text-[9px]">Confirm</button>
                    <button
                      onClick={openCropModal}
                      className="bg-gold/15 hover:bg-gold/25 text-gold py-2.5 px-5 text-[9px] font-black uppercase tracking-widest rounded-lg border border-gold/40 flex items-center gap-1.5 transition-all"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 3v12a2 2 0 002 2h12M3 7h12a2 2 0 012 2v12" />
                      </svg>
                      Crop
                    </button>
                    {isCropped && (
                      <button
                        onClick={resetToOriginalImage}
                        className="bg-white/5 hover:bg-white/10 text-azure-400 py-2.5 px-4 text-[9px] font-black uppercase tracking-widest rounded-lg border border-azure/30 transition-all"
                      >
                        Reset Original
                      </button>
                    )}
                    <button onClick={clearPendingSelection} className="bg-white/5 hover:bg-white/10 text-gray-300 py-2.5 px-5 text-[9px] font-black uppercase tracking-widest rounded-lg border border-white/5">Cancel</button>
                </div>
              </div>
            </div>
          )}

          {showCropModal && filePreview && (
            <div className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4 animate-fade-in">
              <div className="w-full max-w-xl glass-panel border-gold/40 rounded-3xl p-5 md:p-7 shadow-[0_0_80px_rgba(0,0,0,0.9)] flex flex-col max-h-[92vh]">
                <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-4">
                  <div>
                    <span className="text-[8px] font-black uppercase tracking-[0.3em] text-gold block mb-1">Garment Framing Studio</span>
                    <h4 className="text-sm font-black uppercase tracking-widest text-white">Crop Apparel Image</h4>
                  </div>
                  <button
                    onClick={() => setShowCropModal(false)}
                    className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-all"
                  >
                    ✕
                  </button>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <span className="text-[9px] text-gray-400 uppercase tracking-widest">
                    Drag box or corners to isolate garment (crop out faces/background)
                  </span>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => applyCropPreset('3:4')}
                      className="px-2.5 py-1 rounded bg-white/5 hover:bg-gold/20 border border-white/10 hover:border-gold/40 text-[8px] font-black uppercase tracking-widest text-gray-300 hover:text-gold transition-all"
                    >
                      3:4 Dress
                    </button>
                    <button
                      type="button"
                      onClick={() => applyCropPreset('1:1')}
                      className="px-2.5 py-1 rounded bg-white/5 hover:bg-gold/20 border border-white/10 hover:border-gold/40 text-[8px] font-black uppercase tracking-widest text-gray-300 hover:text-gold transition-all"
                    >
                      1:1 Square
                    </button>
                    <button
                      type="button"
                      onClick={() => applyCropPreset('free')}
                      className="px-2.5 py-1 rounded bg-white/5 hover:bg-gold/20 border border-white/10 hover:border-gold/40 text-[8px] font-black uppercase tracking-widest text-gray-300 hover:text-gold transition-all"
                    >
                      Center
                    </button>
                    <button
                      type="button"
                      onClick={() => applyCropPreset('full')}
                      className="px-2.5 py-1 rounded bg-white/5 hover:bg-gold/20 border border-white/10 hover:border-gold/40 text-[8px] font-black uppercase tracking-widest text-gray-300 hover:text-gold transition-all"
                    >
                      Full
                    </button>
                  </div>
                </div>

                <div
                  className="relative flex-1 min-h-[260px] max-h-[55vh] bg-black/70 rounded-2xl border border-white/10 overflow-hidden flex items-center justify-center select-none touch-none"
                  style={{ touchAction: 'none' }}
                  onMouseMove={(e) => handleCropPointerMove(e.clientX, e.clientY)}
                  onMouseUp={handleCropPointerUp}
                  onMouseLeave={handleCropPointerUp}
                  onTouchMove={(e) => {
                    if (e.touches[0]) handleCropPointerMove(e.touches[0].clientX, e.touches[0].clientY);
                  }}
                  onTouchEnd={handleCropPointerUp}
                  onTouchCancel={handleCropPointerUp}
                >
                  <div className="relative inline-block max-h-[52vh] max-w-full" style={{ touchAction: 'none' }}>
                    <img
                      ref={cropImgRef}
                      src={originalPreview || filePreview}
                      alt="Crop Target"
                      draggable={false}
                      className="max-h-[52vh] max-w-full w-auto h-auto object-contain block pointer-events-none select-none"
                    />

                    {/* Darkened exterior mask */}
                    <div
                      className="absolute border-2 border-gold shadow-[0_0_0_9999px_rgba(0,0,0,0.65)] cursor-move"
                      style={{
                        left: `${cropRect.x}%`,
                        top: `${cropRect.y}%`,
                        width: `${cropRect.width}%`,
                        height: `${cropRect.height}%`,
                        touchAction: 'none',
                      }}
                      onMouseDown={(e) => handleCropPointerDown('move', e.clientX, e.clientY, e)}
                      onTouchStart={(e) => {
                        if (e.touches[0]) handleCropPointerDown('move', e.touches[0].clientX, e.touches[0].clientY, e);
                      }}
                    >
                      {/* Rule of thirds grid lines */}
                      <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none">
                        <div className="border-r border-b border-gold/30" />
                        <div className="border-r border-b border-gold/30" />
                        <div className="border-b border-gold/30" />
                        <div className="border-r border-b border-gold/30" />
                        <div className="border-r border-b border-gold/30" />
                        <div className="border-b border-gold/30" />
                        <div className="border-r border-gold/30" />
                        <div className="border-r border-gold/30" />
                        <div />
                      </div>

                      {/* Corner handles (28px touch area on mobile, 20px on desktop) */}
                      <div
                        onMouseDown={(e) => handleCropPointerDown('nw', e.clientX, e.clientY, e)}
                        onTouchStart={(e) => {
                          if (e.touches[0]) handleCropPointerDown('nw', e.touches[0].clientX, e.touches[0].clientY, e);
                        }}
                        className="absolute -top-3.5 -left-3.5 md:-top-2.5 md:-left-2.5 w-7 h-7 md:w-5 md:h-5 bg-gold rounded-full border-2 border-black shadow-md cursor-nwse-resize"
                      />
                      <div
                        onMouseDown={(e) => handleCropPointerDown('ne', e.clientX, e.clientY, e)}
                        onTouchStart={(e) => {
                          if (e.touches[0]) handleCropPointerDown('ne', e.touches[0].clientX, e.touches[0].clientY, e);
                        }}
                        className="absolute -top-3.5 -right-3.5 md:-top-2.5 md:-right-2.5 w-7 h-7 md:w-5 md:h-5 bg-gold rounded-full border-2 border-black shadow-md cursor-nesw-resize"
                      />
                      <div
                        onMouseDown={(e) => handleCropPointerDown('sw', e.clientX, e.clientY, e)}
                        onTouchStart={(e) => {
                          if (e.touches[0]) handleCropPointerDown('sw', e.touches[0].clientX, e.touches[0].clientY, e);
                        }}
                        className="absolute -bottom-3.5 -left-3.5 md:-bottom-2.5 md:-left-2.5 w-7 h-7 md:w-5 md:h-5 bg-gold rounded-full border-2 border-black shadow-md cursor-nesw-resize"
                      />
                      <div
                        onMouseDown={(e) => handleCropPointerDown('se', e.clientX, e.clientY, e)}
                        onTouchStart={(e) => {
                          if (e.touches[0]) handleCropPointerDown('se', e.touches[0].clientX, e.touches[0].clientY, e);
                        }}
                        className="absolute -bottom-3.5 -right-3.5 md:-bottom-2.5 md:-right-2.5 w-7 h-7 md:w-5 md:h-5 bg-gold rounded-full border-2 border-black shadow-md cursor-nwse-resize"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 pt-5 mt-4 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => setShowCropModal(false)}
                    className="py-2.5 px-5 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 text-[9px] font-black uppercase tracking-widest border border-white/10"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyCrop}
                    className="btn-azure py-2.5 px-7 text-[9px]"
                  >
                    Apply Crop
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="py-20 text-center opacity-30">
               <div className="w-12 h-12 border-2 border-azure/30 border-t-azure rounded-full animate-spin mx-auto mb-4"></div>
               <p className="text-[10px] uppercase tracking-[0.5em]">Syncing Digital Closet...</p>
            </div>
          ) : shelves.length === 0 ? (
            <div className="h-[320px] md:h-[450px] flex flex-col items-center justify-center text-center animate-fade-in">
                <div className="relative mb-8">
                    <div className="w-24 h-24 md:w-32 md:h-32 border border-dashed border-azure/20 rounded-full flex items-center justify-center animate-spin-slow">
                        <svg className="w-10 h-10 md:w-12 md:h-12 text-azure-500/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
                        </svg>
                    </div>
                    <div className="absolute inset-0 bg-azure-500/5 blur-3xl rounded-full animate-pulse"></div>
                </div>
                <h3 className="text-[10px] font-black text-white uppercase tracking-[0.5em] mb-2">Vault Status: Empty</h3>
                <p className="text-[10px] font-black text-gray-500 uppercase tracking-[0.5em] max-w-sm leading-relaxed">
                    No logical objects detected in your digital twin closet.<br/>Begin digitization to enable AI-powered orchestration.
                </p>
            </div>
          ) : (
            <div className="flex flex-col items-center animate-fade-in">
                {/* Shelf Navigator */}
                <div className="w-full max-w-sm flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <span className="text-[9px] font-black text-azure-400 uppercase tracking-[0.3em]">Shelf {String(currentShelf + 1).padStart(2, '0')}</span>
                        <span className="text-[8px] font-black text-gray-600 uppercase tracking-widest">[ {activeShelfItems.length} / {itemsPerShelf} Assets ]</span>
                    </div>
                    <div className="flex gap-2">
                        {shelves.map((_, idx) => (
                            <button 
                                key={idx}
                                onClick={() => { setCurrentShelf(idx); setSelectedItemId(null); }}
                                className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${currentShelf === idx ? 'bg-azure w-6 shadow-[0_0_8px_#3b82f6]' : 'bg-white/10 hover:bg-white/20'}`}
                            />
                        ))}
                    </div>
                </div>

                <div 
                    className="relative w-full h-[320px] md:h-[450px] flex justify-center perspective-1000 group"
                    onMouseEnter={() => setIsHoveringStack(true)}
                    onMouseLeave={() => setIsHoveringStack(false)}
                >
                {activeShelfItems.map((item, idx) => {
                    const isSelected = selectedItemId === item.id;
                    const isHovered = hoveredItemId === item.id;
                    const middleIndex = (activeShelfItems.length - 1) / 2;
                    const offset = idx - middleIndex;
                    
                    const spreadMultiplier = isHoveringStack ? (isMobile ? 40 : 80) : (isMobile ? 12 : 25);
                    const rotationMultiplier = isHoveringStack ? 2 : 4;
                    const translateX = offset * spreadMultiplier;
                    const rotation = offset * rotationMultiplier;
                    
                    return (
                        <div 
                        key={item.id}
                        onMouseEnter={() => setHoveredItemId(item.id)}
                        onMouseLeave={() => setHoveredItemId(null)}
                        onClick={() => { setSelectedItemId(item.id); setIsEditing(false); }}
                        className={`absolute w-40 h-60 md:w-56 md:h-80 glass-panel rounded-xl md:rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ease-out
                            ${isSelected ? 'z-[60] scale-110 -translate-y-6 md:-translate-y-12 shadow-[0_20px_40px_-12px_rgba(59,130,246,0.5)] border-azure-400' : ''}
                            ${isHovered && !isSelected ? 'z-[55] -translate-y-10 md:-translate-y-20 scale-125 shadow-2xl border-white/30' : 'z-10'}
                        `}
                        style={{
                            transform: isSelected 
                                ? `rotate(0deg) translateX(0)` 
                                : isHovered 
                                    ? `rotate(0deg) translateX(${translateX}px)`
                                    : `rotate(${rotation}deg) translateX(${translateX}px)`,
                            left: isMobile ? 'calc(50% - 5rem)' : 'calc(50% - 7rem)',
                            opacity: isSelected || isHovered || !isHoveringStack ? 1 : 0.6,
                            filter: isHoveringStack && !isHovered && !isSelected ? 'blur(2px) grayscale(0.5)' : 'none'
                        }}
                        >
                        <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover pointer-events-none" />
                        <div className="absolute inset-0 bg-gradient-to-t from-dk-900 via-transparent to-transparent opacity-60"></div>
                        <div className="absolute bottom-4 left-4 right-4 text-center">
                            <p className="text-[9px] font-black text-white uppercase tracking-tighter truncate leading-none">{item.name}</p>
                        </div>
                        </div>
                    )
                })}
                </div>
            </div>
          )}
        </div>

        <div className="flex-grow">
          <div className="glass-panel rounded-2xl p-8 shadow-2xl overflow-hidden min-h-[300px]">
             {selectedItem ? (
               <div className="animate-fade-in space-y-8">
                  {isEditing ? (
                    /* Edit Form */
                    <div className="space-y-8">
                        <header className="flex justify-between items-center border-b border-white/5 pb-6">
                            <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">Object Refinement</h2>
                            <div className="flex gap-4">
                                <button onClick={() => setIsEditing(false)} className="text-[10px] font-black uppercase text-gray-500 hover:text-white transition-colors">Cancel</button>
                                <button onClick={handleEditSave} className="text-[10px] font-black uppercase text-azure-400 hover:text-azure-300 transition-colors underline decoration-2 underline-offset-4">Save Logic</button>
                            </div>
                        </header>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                            <div className="aspect-square rounded-xl overflow-hidden border border-white/10 bg-dk-800">
                                <img src={selectedItem.imageUrl} alt={selectedItem.name} className="w-full h-full object-cover opacity-50" />
                            </div>
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[9px] font-black uppercase text-gray-500 mb-2 tracking-widest">Object Identity</label>
                                    <input 
                                        className="w-full input-glass text-xs cursor-text" 
                                        value={editForm?.name} 
                                        onChange={e => setEditForm(prev => prev ? { ...prev, name: e.target.value } : null)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase text-gray-500 mb-2 tracking-widest">Logic Category</label>
                                    <select 
                                        className="w-full input-glass text-xs"
                                        value={editForm?.category}
                                        onChange={e => setEditForm(prev => prev ? { ...prev, category: e.target.value as any } : null)}
                                    >
                                        {EDIT_CATEGORIES.map(c => <option key={c} value={c} className="bg-dk-800">{c}</option>)}
                                    </select>
                                </div>
                                <div className="p-4 bg-white/5 rounded-xl border border-white/10 space-y-6">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-azure-400">Visual DNA</p>
                                    <div>
                                        <label className="block text-[9px] font-black uppercase text-gray-400 mb-2 tracking-widest">Primary Color</label>
                                        <input 
                                            className="w-full input-glass text-xs cursor-text" 
                                            value={editForm?.visual.primaryColor} 
                                            onChange={e => setEditForm(prev => prev ? { ...prev, visual: { ...prev.visual, primaryColor: e.target.value } } : null)}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[9px] font-black uppercase text-gray-400 mb-2 tracking-widest">Pattern</label>
                                        <input 
                                            className="w-full input-glass text-xs cursor-text" 
                                            value={editForm?.visual.pattern} 
                                            onChange={e => setEditForm(prev => prev ? { ...prev, visual: { ...prev.visual, pattern: e.target.value } } : null)}
                                        />
                                    </div>
                                </div>

                                <div className="p-4 bg-white/5 rounded-xl border border-white/10 space-y-6">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-azure-400">Fabric Properties</p>
                                    <div>
                                        <label className="block text-[9px] font-black uppercase text-gray-400 mb-2 tracking-widest">Composition</label>
                                        <input 
                                            className="w-full input-glass text-xs cursor-text" 
                                            value={editForm?.fabric.composition} 
                                            onChange={e => setEditForm(prev => prev ? { ...prev, fabric: { ...prev.fabric, composition: e.target.value } } : null)}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[9px] font-black uppercase text-gray-400 mb-2 tracking-widest">Breathability Index ({editForm?.fabric.breathabilityIndex}/10)</label>
                                        <input 
                                            type="range" min="1" max="10"
                                            className="w-full accent-azure" 
                                            value={editForm?.fabric.breathabilityIndex} 
                                            onChange={e => setEditForm(prev => prev ? { ...prev, fabric: { ...prev.fabric, breathabilityIndex: parseInt(e.target.value) } } : null)}
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-[9px] font-black uppercase text-gray-400 mb-2 tracking-widest">Cultural Signature</label>
                                    <input 
                                        className="w-full input-glass text-xs cursor-text" 
                                        placeholder="e.g. south-asian, wedding, festive"
                                        value={culturalTagsInput} 
                                        onChange={e => setCulturalTagsInput(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase text-gray-400 mb-2 tracking-widest">Formality Index ({editForm?.formality}/10)</label>
                                    <input 
                                        type="range" min="1" max="10"
                                        className="w-full accent-azure" 
                                        value={editForm?.formality} 
                                        onChange={e => setEditForm(prev => prev ? { ...prev, formality: parseInt(e.target.value) } : null)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                  ) : (
                    /* Display View */
                    <>
                        <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 border-b border-white/5 pb-6">
                            <div>
                                <span className="attribute-badge text-azure-400 bg-azure/5 border-azure/20 mb-2 inline-block">{selectedItem.category}</span>
                                <h2 className="text-2xl font-black text-white italic tracking-tighter uppercase mb-1">{selectedItem.name}</h2>
                                <p className="text-[11px] text-gray-500 font-bold uppercase tracking-[0.2em]">{selectedItem.fabric.composition}</p>
                            </div>
                            <div className="flex gap-6">
                                <button onClick={handleEditInit} className="text-azure-400 hover:text-azure-300 text-[10px] font-black uppercase tracking-tighter transition-colors">
                                    [ Refine Metadata ]
                                </button>
                                <button onClick={() => handleDelete(selectedItem.id)} className="text-red-500 hover:text-red-400 text-[10px] font-black uppercase tracking-tighter transition-colors">
                                    [ Terminate ]
                                </button>
                            </div>
                        </header>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                            <div className="aspect-square rounded-xl overflow-hidden border border-white/10 bg-dk-800 shadow-2xl group">
                                <img src={selectedItem.imageUrl} alt={selectedItem.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                            </div>

                            <div className="space-y-8">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-6 bg-white/5 rounded-2xl border border-white/5">
                                        <p className="text-[9px] text-gray-500 uppercase font-black mb-4 tracking-widest">Breathability</p>
                                        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                                        <div className="h-full bg-azure-500 shadow-[0_0_8px_#3b82f6]" style={{ width: `${selectedItem.fabric.breathabilityIndex * 10}%` }}></div>
                                        </div>
                                    </div>
                                    <div className="p-6 bg-white/5 rounded-2xl border border-white/5">
                                        <p className="text-[9px] text-gray-500 uppercase font-black mb-2 tracking-widest">Formality</p>
                                        <p className="text-3xl font-black text-gold italic">{selectedItem.formality}<span className="text-[10px] text-gray-600 ml-1">/10</span></p>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div>
                                        <p className="text-[9px] text-gray-500 uppercase font-black mb-3 tracking-[0.3em]">Visual DNA</p>
                                        <div className="flex gap-3">
                                        <span className="text-[10px] font-bold bg-white/5 px-4 py-2 rounded-lg border border-white/10 text-gray-300 uppercase tracking-tighter">{selectedItem.visual.primaryColor}</span>
                                        <span className="text-[10px] font-bold bg-white/5 px-4 py-2 rounded-lg border border-white/10 text-gray-300 uppercase tracking-tighter">{selectedItem.visual.pattern}</span>
                                        </div>
                                    </div>
                                    {selectedItem.culturalTags && selectedItem.culturalTags.length > 0 && (
                                    <div>
                                        <p className="text-[9px] text-gray-500 uppercase font-black mb-3 tracking-[0.3em]">Cultural Signature</p>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedItem.culturalTags.map(tag => (
                                            <span key={tag} className="attribute-badge text-azure-400 border-azure-400/20 lowercase italic">#{tag}</span>
                                            ))}
                                        </div>
                                    </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                  )}
               </div>
             ) : (
               <div className="h-full min-h-[300px] flex flex-col items-center justify-center text-center opacity-30">
                  <div className="w-16 h-16 border-2 border-dashed border-white/10 rounded-full mb-6 animate-spin-slow"></div>
                  <p className="text-[10px] font-black uppercase tracking-[0.5em] leading-relaxed">Object Selection Required<br/>to Decode Metadata</p>
               </div>
             )}
          </div>
        </div>
      </div>

      {/* 5-Second Deletion Safeguard Undo HUD */}
      {pendingDeletion && (
        <div className="fixed bottom-20 md:bottom-8 left-1/2 -translate-x-1/2 z-[9999] w-[92%] max-w-md bg-[#05070c]/95 border-2 border-azure-500/80 p-4 rounded-2xl shadow-[0_0_50px_rgba(0,127,255,0.3)] flex items-center justify-between gap-4 animate-slide-up backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400 shrink-0">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <div className="text-left">
              <p className="text-xs font-black text-white uppercase tracking-wider truncate max-w-[180px]">
                {pendingDeletion.item.name}
              </p>
              <p className="text-[10px] text-gray-400 font-mono">
                Permanent deletion in <span className="text-azure-400 font-bold">{pendingDeletion.secondsRemaining}s</span>
              </p>
            </div>
          </div>
          <button
            onClick={handleUndoDelete}
            className="btn-azure text-[11px] font-black uppercase tracking-widest py-2 px-5 rounded-xl shadow-lg hover:scale-105 transition-all shrink-0 cursor-pointer"
          >
            ↺ Undo
          </button>
        </div>
      )}
    </div>
  );
};

export default WardrobeManager;
